import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { C, radius, card, shadow } from '../styles';
import { useAuth } from '../context/AuthContext';
import { useJobs } from '../context/JobsContext';
import JobCard from '../components/JobCard';

const API = 'https://reportautomation.finkraftai.com/api';

const REPORT_TYPE_INFO = {
  '2b': {
    label: '2B — GST Portal Match',
    badge: 'IN 2B',
    badgeColor: C.accent2,
    badgeBg:    C.accentBg,
    description: 'Reconcile invoices matched in GSTR-2B. Runs full 26-flag engine including booking, GSTIN combo & head checks.',
    flags: '26 error flags',
    filter: '2A/2B Status = In 2B',
  },
  '2a': {
    label: '2A — Booking Wise',
    badge: 'IN 2A',
    badgeColor: C.success,
    badgeBg:    C.successBg,
    description: 'Reconcile booking-wise data against GSTR-2A. Focuses on GSTIN, invoice & GST variance validation.',
    flags: '15 core flags',
    filter: '2A/2B Status = In 2A',
  },
};

export default function RunPage() {
  const { user } = useAuth();
  const { jobList, submitJob } = useJobs();

  const [reportType,  setReportType]  = useState('2b');
  const [file,        setFile]        = useState(null);
  const [dragOver,    setDragOver]    = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [toast,       setToast]       = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4500);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) setFile(f);
  };

  const handleSubmit = async () => {
    if (!file) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('report_type', reportType);
      formData.append('client', 'default');
      formData.append('username', user.username);
      formData.append('file', file);
      const res = await axios.post(`${API}/run`, formData);
      const run_id = res.data.run_id;
      submitJob && submitJob('default', file, user.username, reportType, run_id);
      setToast({ msg: `Run started (${REPORT_TYPE_INFO[reportType].badge}) — ID ${run_id.slice(-8)}. You can start another run while this processes.`, type: 'ok' });
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      setToast({ msg: e?.response?.data?.error || e.message || 'Failed to start run', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const meta = REPORT_TYPE_INFO[reportType];

  return (
    <div style={{ display: 'flex', gap: 20, height: '100%' }}>

      {/* ── LEFT: form ── */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontFamily: 'Syne,sans-serif', fontSize: 22, fontWeight: 600,
                       color: C.text, marginBottom: 5 }}>
            New Reconciliation Run
          </h2>
          <p style={{ color: C.muted, fontSize: 13 }}>
            Each run is independent — start multiple runs simultaneously.
          </p>
        </div>

        {/* Step 1 — Report Type Toggle */}
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
            <div style={{
              width:22, height:22, borderRadius:'50%',
              background: `linear-gradient(135deg, ${C.accent}, ${C.teal})`,
              color:'#fff', fontSize:11, fontWeight:700,
              display:'grid', placeItems:'center',
            }}>1</div>
            <span style={{ fontSize: 12, color: C.textSoft, textTransform: 'uppercase',
                           letterSpacing: '.08em', fontWeight: 600 }}>
              Select Report Type
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {Object.entries(REPORT_TYPE_INFO).map(([key, info]) => {
              const selected = reportType === key;
              return (
                <div
                  key={key}
                  onClick={() => setReportType(key)}
                  style={{
                    border: `2px solid ${selected ? C.accent : C.border2}`,
                    borderRadius: radius.md,
                    padding: '14px 16px',
                    cursor: 'pointer',
                    background: selected ? C.accentBg : C.surface2,
                    transition: 'all .15s',
                    position: 'relative',
                    boxShadow: selected ? shadow.sm : 'none',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <div style={{
                      width: 18, height: 18, borderRadius: '50%',
                      border: `2px solid ${selected ? C.accent : C.mutedSoft}`,
                      background: selected ? C.accent : 'transparent',
                      display: 'grid', placeItems: 'center', flexShrink: 0,
                      transition:'all .15s',
                    }}>
                      {selected && <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white' }} />}
                    </div>
                    <span style={{
                      fontSize: 13, fontWeight: 700,
                      color: selected ? C.accent2 : C.text,
                      fontFamily: 'Inter,sans-serif',
                    }}>
                      {info.label}
                    </span>
                    <span style={{
                      marginLeft: 'auto',
                      fontSize: 10, fontWeight: 700, letterSpacing: '.05em',
                      color: info.badgeColor, background: info.badgeBg,
                      border: `1px solid ${info.badgeColor}33`,
                      padding: '2px 7px', borderRadius: 20,
                    }}>
                      {info.badge}
                    </span>
                  </div>
                  <p style={{ fontSize: 12, color: C.muted, margin: 0, lineHeight: 1.55 }}>
                    {info.description}
                  </p>
                </div>
              );
            })}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {[
              ['Filter Applied', meta.filter],
              ['Rules Engine',   meta.flags],
            ].map(([k, v]) => (
              <div key={k} style={{ background: C.surface2,
                                     border:`1px solid ${C.border}`,
                                     borderRadius: 8, padding: '9px 12px' }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 3,
                              textTransform:'uppercase', letterSpacing:'.05em', fontWeight:600 }}>{k}</div>
                <div style={{ fontSize: 12.5, color: C.text, fontWeight:500 }}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Step 2 — file upload */}
        <div style={{ ...card, marginBottom: 16 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
            <div style={{
              width:22, height:22, borderRadius:'50%',
              background: `linear-gradient(135deg, ${C.accent}, ${C.teal})`,
              color:'#fff', fontSize:11, fontWeight:700,
              display:'grid', placeItems:'center',
            }}>2</div>
            <span style={{ fontSize: 12, color: C.textSoft, textTransform: 'uppercase',
                           letterSpacing: '.08em', fontWeight: 600 }}>
              Upload Data File
            </span>
          </div>

          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            style={{
              border: `2px dashed ${file ? C.success : dragOver ? C.accent : C.border2}`,
              borderRadius: radius.md, padding: '34px 20px', textAlign: 'center',
              cursor: 'pointer', transition: 'all .2s',
              background: file ? C.successBg : dragOver ? C.accentBg : C.surface2,
            }}
          >
            <div style={{
              width: 48, height: 48, borderRadius: 12, margin: '0 auto 14px',
              background: file ? C.successBg : C.accentBg,
              border: `1px solid ${file ? C.success + '44' : C.accent + '33'}`,
              display: 'grid', placeItems: 'center',
            }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                   stroke={file ? C.success : C.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                {file
                  ? <><circle cx="12" cy="12" r="9"/><polyline points="9 12 11 14 15 10"/></>
                  : <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>
                }
              </svg>
            </div>
            {file ? (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, color: C.success, marginBottom: 4 }}>{file.name}</div>
                <div style={{ fontSize: 12, color: C.muted }}>{(file.size / 1024 / 1024).toFixed(2)} MB · Click to change</div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4, color: C.text }}>
                  Drop file here or click to browse
                </div>
                <div style={{ fontSize: 12, color: C.muted }}>Supports .xlsx, .xls, .csv</div>
              </>
            )}
          </div>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv"
                 style={{ display: 'none' }}
                 onChange={e => e.target.files[0] && setFile(e.target.files[0])} />
        </div>

        {/* Step 3 — submit */}
        <button
          onClick={handleSubmit}
          disabled={!file || submitting}
          style={{
            background: (!file || submitting)
              ? C.surface3
              : `linear-gradient(135deg, ${C.accent} 0%, ${C.teal} 100%)`,
            border: 'none', borderRadius: radius.md, padding: '12px 30px',
            color: (!file || submitting) ? C.muted : 'white',
            fontFamily: 'Inter,sans-serif', fontSize: 14, fontWeight: 600,
            letterSpacing:'.02em',
            cursor: (!file || submitting) ? 'not-allowed' : 'pointer',
            transition: 'all .2s',
            boxShadow: (!file || submitting) ? 'none' : '0 4px 12px rgba(8,145,178,0.25)',
          }}
          onMouseEnter={e => { if (file && !submitting) e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          {submitting ? '▶ Starting…' : `▶ Run ${meta.badge} Reconciliation`}
        </button>
        <p style={{ fontSize: 11.5, color: C.muted, marginTop: 10 }}>
          You can start another run immediately while this one processes in the background.
        </p>
      </div>

      {/* ── RIGHT: active jobs panel ── */}
      <div style={{ width: 360, flexShrink: 0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom: 14 }}>
          <h3 style={{ fontFamily: 'Inter,sans-serif', fontSize: 12, fontWeight: 700,
                       color: C.muted, textTransform:'uppercase', letterSpacing:'.08em', margin:0 }}>
            Active & Recent Jobs
          </h3>
          {jobList.length > 0 && (
            <span style={{ background: C.accentBg, color: C.accent2,
                           fontSize: 11, padding: '2px 8px', borderRadius: 20, fontWeight:600,
                           border: `1px solid ${C.accent}33` }}>
              {jobList.length}
            </span>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10,
                      maxHeight: 'calc(100vh - 150px)', overflowY: 'auto',
                      paddingRight: 4 }}>
          {jobList.length === 0 ? (
            <div style={{ background: C.surface, borderRadius: radius.md,
                          padding: '28px 20px', textAlign: 'center', color: C.muted,
                          fontSize: 13, border: `1px dashed ${C.border2}` }}>
              <div style={{ fontSize:24, marginBottom:6, opacity:.5 }}>⊕</div>
              No runs yet — submit a job to see it here
            </div>
          ) : (
            jobList.map(job => (
              <JobCard key={job.run_id} job={job} compact={false} />
            ))
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 999,
          background: C.surface,
          border: `1px solid ${toast.type === 'ok' ? C.success : C.danger}66`,
          borderLeft: `4px solid ${toast.type === 'ok' ? C.success : C.danger}`,
          color: C.text,
          borderRadius: radius.md, padding: '12px 18px', fontSize: 13,
          animation: 'slideUp .2s ease', maxWidth: 400,
          boxShadow: shadow.lg,
        }}>
          <div style={{ fontWeight:600, color: toast.type === 'ok' ? C.success : C.danger,
                        marginBottom: 2, fontSize:12 }}>
            {toast.type === 'ok' ? '✓ Success' : '✗ Error'}
          </div>
          {toast.msg}
        </div>
      )}
    </div>
  );
}